import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { formatResetTime } from "./resetTime";
import { glassPanelStyle, tokens } from "./styles";

// Estimated-spend threshold at which a meter turns amber, matching the
// governance warn fraction (80%).
const WARN_FRACTION = 0.8;
const AMBER = "#FCD34D";

function dollars(micros) {
  return `$${(Number(micros || 0) / 1_000_000).toFixed(2)}`;
}

function Meter({ label, valueText, fraction, unlimited, accent }) {
  const pct = Math.min(100, Math.max(0, Math.round(fraction * 100)));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          color: tokens.ink[200],
        }}
      >
        <span>{label}</span>
        <span style={{ color: tokens.ink[300] }}>{valueText}</span>
      </div>
      {unlimited ? null : (
        <div
          style={{
            height: 5,
            borderRadius: 999,
            background: tokens.surface.panelLight,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: accent,
              borderRadius: 999,
              transition: "width 0.4s ease",
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Claude-style usage meter for the copilot panel: daily voice minutes, an
 * estimated-spend bar, the tier, and when it resets.
 *
 * The trigger is a compact bar pinned in the footer. The details open as a
 * floating popover anchored ABOVE the bar (position: absolute, bottom:
 * 100%) instead of an inline block, so expanding it never grows the footer
 * or pushes the transcript/controls around — it simply floats over them.
 * The popover spans the trigger's width, so it inherits the panel's
 * responsive sizing and stays usable on small viewports. Closes on an
 * outside click or Escape. Renders nothing until the first snapshot arrives.
 */
export function UsageSection({ usage }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Dismiss the popover on an outside click or Escape while it's open. Bound
  // only while open so we don't hold global listeners for a collapsed meter.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!usage) return null;

  const {
    tier,
    minutes_used: minutesUsed = 0,
    minutes_limit: minutesLimit = 0,
    cost_used_micros: costUsed = 0,
    cost_limit_micros: costLimit = 0,
    resets_at: resetsAt,
    authenticated,
    enforcement_mode: enforcementMode,
  } = usage;

  const minutesUnlimited = !minutesLimit; // 0 => unlimited
  const costUnlimited = !costLimit;
  const minFrac = minutesUnlimited ? 0 : minutesUsed / minutesLimit;
  const costFrac = costUnlimited ? 0 : costUsed / costLimit;
  const minAccent = minFrac >= WARN_FRACTION ? AMBER : tokens.accent.cyanStrong;
  const costAccent = costFrac >= WARN_FRACTION ? AMBER : tokens.accent.violetStrong;

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "transparent",
          border: `1px solid ${tokens.surface.border}`,
          borderRadius: 12,
          cursor: "pointer",
          color: tokens.ink[100],
          fontSize: 12,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span aria-hidden="true">📊</span>
          Usage
          {tier ? (
            <span
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: tokens.ink[300],
                border: `1px solid ${tokens.surface.border}`,
                borderRadius: 999,
                padding: "1px 6px",
              }}
            >
              {tier}
            </span>
          ) : null}
        </span>
        <span
          aria-hidden="true"
          style={{
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s ease",
            color: tokens.ink[300],
          }}
        >
          ▾
        </span>
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            role="dialog"
            aria-label="Usage details"
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
            style={{
              ...glassPanelStyle,
              position: "absolute",
              // Float above the trigger so opening never reflows the footer.
              bottom: "calc(100% + 8px)",
              left: 0,
              right: 0,
              zIndex: 5,
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              borderRadius: 12,
              // Defensive cap: the popover scrolls internally rather than
              // overflowing the panel on very short viewports.
              maxHeight: 240,
              overflowY: "auto",
            }}
          >
            <Meter
              label="Voice today"
              accent={minAccent}
              unlimited={minutesUnlimited}
              fraction={minFrac}
              valueText={
                minutesUnlimited
                  ? `${minutesUsed} min · unlimited`
                  : `${minutesUsed} / ${minutesLimit} min`
              }
            />
            <Meter
              label="Estimated cost"
              accent={costAccent}
              unlimited={costUnlimited}
              fraction={costFrac}
              valueText={
                costUnlimited
                  ? `${dollars(costUsed)} · unlimited`
                  : `${dollars(costUsed)} / ${dollars(costLimit)}`
              }
            />
            <div style={{ fontSize: 11, color: tokens.ink[300] }}>
              Resets {formatResetTime(resetsAt) || "at the daily reset"}.
            </div>
            {authenticated ? null : (
              <div style={{ fontSize: 10, color: tokens.ink[400] }}>
                Daily totals track once you&rsquo;re signed in.
              </div>
            )}
            {enforcementMode === "observe" ? (
              <div style={{ fontSize: 10, color: tokens.ink[400] }}>
                Limits are informational right now — nothing is blocked.
              </div>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
